import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { captureSignatureMetadata, CONSENT_TEXT } from "@/lib/captureSignatureMetadata";
import { useToast } from "@/lib/useToast";
import { Toast, SignatureCelebration, SignatureLoadingModal, SignatureSuccessModal } from "@/components/ui";
import { getBrandConfig, Brand } from "@/lib/brandConfig";

interface Quote {
  id: string;
  case_id: string;
  file_url: string;
  version: number;
  signed: boolean;
  signature_token: string | null;
  signed_at: string | null;
  created_at: string;
  display_name: string | null;
}

interface PublicContextResponse {
  quote: Quote;
  allQuotes: Quote[];
  threadId: string | null;
  brand: Brand;
}

function PublicMessageBox({ token }: { token: string }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  const handleSend = async () => {
    if (!message.trim()) {
      showToast({ message: "Veuillez saisir un message", type: "warning" });
      return;
    }

    setSending(true);
    try {
      const response = await fetch("/api/signature/public-message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          message: message.trim(),
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || "Erreur lors de l'envoi du message");
      }

      setSent(true);
      setMessage("");
      setTimeout(() => setSent(false), 3000);
      showToast({ message: "Message envoyé avec succès !", type: "success" });
    } catch (err) {
      console.error("Error sending message:", err);
      showToast({ message: "Erreur lors de l'envoi du message", type: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="mt-6 w-full border-t pt-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          Envoyer un message à l'équipe
        </h3>
        {sent ? (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3">
            <p className="text-green-700 text-sm font-medium">✓ Message envoyé !</p>
          </div>
        ) : (
          <>
            <textarea
              className="w-full rounded-md border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Posez vos questions ou ajoutez des informations..."
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="mt-2 w-full rounded-lg bg-indigo-600 py-2 text-white font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {sending ? "Envoi en cours..." : "Envoyer le message"}
            </button>
          </>
        )}
      </div>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={toast.duration}
      />
    </>
  );
}

export default function PublicSignPage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [allQuotes, setAllQuotes] = useState<Quote[]>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  
  // États pour les animations de signature
  const [showCelebration, setShowCelebration] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null);
  
  // Brand du case (pour adapter les textes Smile/Laser)
  const [brand, setBrand] = useState<Brand>('smile');
  const brandConfig = getBrandConfig(brand);
  // Helper pour afficher le nom du devis (display_name prioritaire)
  const getQuoteName = (q: Quote) => {
    return q.display_name || `Version ${q.version}`;
  };

  useEffect(() => {
    if (!token) {
      setError("Token manquant");
      setLoading(false);
      return;
    }

    const fetchQuote = async () => {
      try {
        const response = await fetch(`/api/signature/public-context?token=${encodeURIComponent(token)}`);
        const result = (await response.json()) as Partial<PublicContextResponse> & { message?: string };

        if (!response.ok || !result.quote) {
          throw new Error(result.message || "Devis introuvable ou lien invalide");
        }

        setQuote(result.quote);
        setAllQuotes(result.allQuotes ?? []);
        setSelectedQuoteId(result.allQuotes?.[0]?.id ?? result.quote.id);
        setBrand(result.brand ?? "smile");
      } catch (err) {
        console.error("Error fetching quote:", err);
        setError(err instanceof Error ? err.message : "Erreur lors du chargement du devis");
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [token]);

  const handleSign = async () => {
    if (!selectedQuoteId) {
      showToast({ message: "Veuillez sélectionner un devis à signer", type: "warning" });
      return;
    }

    if (!clientName.trim()) {
      showToast({ message: "Veuillez saisir votre nom complet", type: "warning" });
      return;
    }

    if (!clientPhone.trim()) {
      showToast({ message: "Veuillez saisir votre numéro de téléphone", type: "warning" });
      return;
    }

    if (!consentGiven) {
      showToast({ message: "Veuillez accepter les conditions de signature électronique", type: "warning" });
      return;
    }

    setSigning(true);
    setError(null);

    try {
      const selectedQuote = allQuotes.find(q => q.id === selectedQuoteId);
      if (!selectedQuote) throw new Error("Devis introuvable");

      // 🎉 Démarrer les animations !
      setShowCelebration(true);
      setShowLoadingModal(true);
      const response = await fetch("/api/signature/public-submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          selectedQuoteId,
          clientName: clientName.trim(),
          clientPhone: clientPhone.trim(),
          consentGiven,
          metadata: captureSignatureMetadata(),
        }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Erreur lors de la signature");
      }

      setShowLoadingModal(false);
      setShowCelebration(false);

      if (result.signedPdfUrl) {
        setSignedPdfUrl(result.signedPdfUrl);
        setShowSuccessModal(true);
      } else {
        showToast({
          message: `${getQuoteName(selectedQuote)} signé avec succès !`,
          type: "success"
        });
      }

      // Mettre à jour l'état local
      setAllQuotes(prev => prev.map(q => 
        q.id === selectedQuoteId 
          ? { ...q, signed: true, signed_at: new Date().toISOString() }
          : q
      ));
      
      // Réinitialiser le formulaire
      setClientName("");
      setClientPhone("");
      setConsentGiven(false);
    } catch (err) {
      console.error("Error signing quote:", err);
      setShowLoadingModal(false);
      setShowCelebration(false);
      setError(err instanceof Error ? err.message : "Erreur lors de la signature. Veuillez réessayer.");
      showToast({ 
        message: err instanceof Error ? err.message : "Erreur lors de la signature du devis",
        type: "error" 
      });
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#fdfcfb] to-[#e2d1c3]">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#fdfcfb] to-[#e2d1c3]">
        <div className="max-w-md rounded-xl bg-white p-8 shadow-lg text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Lien invalide
          </h1>
          <p className="text-sm text-gray-600">
            {error || "Ce lien de signature n'est pas valide ou a expiré."}
          </p>
        </div>
      </div>
    );
  }

  const selectedQuote = allQuotes.find(q => q.id === selectedQuoteId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fdfcfb] to-[#e2d1c3] p-4">
      <div className="w-full max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Colonne gauche : PDF Viewer */}
          <div className="bg-white rounded-xl shadow-lg p-4 lg:sticky lg:top-4 lg:h-[calc(100vh-2rem)]">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Prévisualisation du devis
            </h2>
            
            {selectedQuote && (
              <div className="mb-4">
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {getQuoteName(selectedQuote)}
                      {selectedQuote.signed && (
                        <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          ✓ Signé
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {new Date(selectedQuote.created_at).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  <a
                    href={selectedQuote.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Ouvrir ↗
                  </a>
                </div>
              </div>
            )}

            {/* PDF Viewer intégré */}
            {selectedQuote ? (
              <iframe
                src={`${selectedQuote.file_url}#toolbar=0&navpanes=0&scrollbar=1`}
                className="w-full h-[calc(100%-8rem)] border border-gray-300 rounded-lg"
                title={getQuoteName(selectedQuote)}
              />
            ) : (
              <div className="flex items-center justify-center h-[calc(100%-8rem)] border border-gray-300 rounded-lg bg-gray-50">
                <p className="text-gray-500">Sélectionnez un devis à visualiser</p>
              </div>
            )}
          </div>

          {/* Colonne droite : Sélection et signature */}
          <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Signature de devis
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            {brandConfig.eventName}
          </p>
        </div>

            {/* Liste de tous les devis disponibles */}
            {allQuotes.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  {allQuotes.length > 1 ? `${allQuotes.length} devis disponibles` : "Devis disponible"}
                </h2>
                <div className="space-y-2">
                  {allQuotes.map((q) => (
                    <button
                      key={q.id}
                      onClick={() => setSelectedQuoteId(q.id)}
                      className={`w-full rounded-lg p-4 border-2 transition-all text-left ${
                        q.id === selectedQuoteId
                          ? "bg-indigo-50 border-indigo-500 shadow-md"
                          : "bg-gray-50 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {getQuoteName(q)}
                            </span>
                            {q.signed && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                ✓ Signé
                              </span>
                            )}
                            {q.id === selectedQuoteId && (
                              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                                Sélectionné
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            Créé le {new Date(q.created_at).toLocaleDateString("fr-FR")} à{" "}
                            {new Date(q.created_at).toLocaleTimeString("fr-FR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                        {q.id === selectedQuoteId && (
                          <div className="ml-4 text-indigo-600">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

        <div className="space-y-4">
          <div>
            <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-1">
              Nom complet *
            </label>
            <input
              id="clientName"
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Jean Dupont"
            />
          </div>

          <div>
            <label htmlFor="clientPhone" className="block text-sm font-medium text-gray-700 mb-1">
              Numéro de téléphone *
            </label>
            <input
              id="clientPhone"
              type="tel"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="06 12 34 56 78"
            />
          </div>

          {brandConfig.cgvLinks?.length ? (
            <section className="rounded-lg border border-gray-300 bg-gray-50 p-4" aria-label="Conditions générales de vente">
              <h3 className="font-semibold text-gray-900">Conditions générales de vente</h3>
              {brandConfig.cgvLinks.map(link => (
                <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="mt-2 block py-2 text-sm font-medium text-indigo-700 underline">{link.label} (PDF)</a>
              ))}
            </section>
          ) : null}

          {/* Consentement RGPD */}
          <div className="pt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="text-xs text-gray-700 leading-relaxed">
                {CONSENT_TEXT}
                {" "}
                <a 
                  href={brandConfig.cgvUrl}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 underline"
                >
                  Consulter les conditions générales de vente
                </a>
              </span>
            </label>
          </div>

          <div className="pt-4">
            <button
              onClick={handleSign}
              disabled={signing || !selectedQuoteId || selectedQuote?.signed}
              className="w-full rounded-lg bg-green-600 py-3 text-white font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {signing 
                ? "Signature en cours..." 
                : selectedQuote?.signed 
                  ? "✅ Devis déjà signé" 
                  : `✍️ Signer ${selectedQuote ? getQuoteName(selectedQuote) : ""}`
              }
            </button>
            
            {selectedQuote?.signed && (
              <p className="text-xs text-center text-green-600 mt-2">
                Ce devis a été signé le {new Date(selectedQuote.signed_at || "").toLocaleDateString("fr-FR")} à{" "}
                {new Date(selectedQuote.signed_at || "").toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            )}
          </div>

          <p className="text-xs text-center text-gray-500 mt-4">
            En signant, vous acceptez les termes et conditions du devis.
          </p>
          
          {/* Avertissement important */}
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800 text-center leading-relaxed">
              <strong>⚠️ Important :</strong> La signature de ce devis ne confirme pas définitivement votre événement. 
              Nous vous enverrons une confirmation une fois la disponibilité de la date et du créneau horaire vérifiée.
            </p>
          </div>

            {/* Zone de message public */}
            <PublicMessageBox token={token || ""} />
          </div>
          </div>
        </div>
      </div>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={toast.duration}
      />
      
      {/* Animations de signature */}
      <SignatureCelebration isActive={showCelebration} />
      <SignatureLoadingModal isOpen={showLoadingModal} />
      <SignatureSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        signedPdfUrl={signedPdfUrl || ""}
        quoteName={selectedQuote ? getQuoteName(selectedQuote) : "Devis"}
      />
    </div>
  );
}
